import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "./ActionPlansTable.styles";
import { IActionPlansTableProps } from "./ActionPlansTableInterfaces";
import { Alert, Card, Snackbar } from "@mui/material";
import MainCardButtonAction from "components/MainCardButtonAction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { forwardRef } from "react";
import NoData from "components/NoData/NoData.view";
import { useTranslation } from "react-i18next";
import useActionPlansTableColumns from "./hooks/useActionPlansTableColumns";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { ActionPlansEditionProvider } from "./context/ActionPlansEditionProvider";
import { ActionPlansMainTable } from "./components/ActionPlansMainTable";
import { ActionPlanInlineForm } from "./components/ActionPlanInlineForm";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import { useActionPlanCreation } from "./hooks/useActionPlanCreation";
import { useActionPlanSnackbars } from "./hooks/useActionPlanSnackbars";
import { useActionPlanDeletion } from "./hooks/useActionPlanDeletion";
import useRolesPermissions from "hooks/useRolesPermissions";

const ActionPlansTable = forwardRef<HTMLDivElement, IActionPlansTableProps>(
    ({ parentType, parentId, rows, loading, millId, onActionPlanCreated }, ref) => {
        const mainClasses = useRCAMainStyles();
        const classes = useStyles();
        const { t } = useTranslation();

        const { RCA_PERMISSIONS } = useRolesPermissions();

        const snackbars = useActionPlanSnackbars();

        const creation = useActionPlanCreation({
            parentType,
            parentId,
            onActionPlanCreated,
            onSuccess: (messageKey) => snackbars.showSuccess(t(messageKey)),
            onError: (messageKey) => snackbars.showError(t(messageKey)),
        });

        const deletion = useActionPlanDeletion({
            onActionPlanCreated,
            onSuccess: (messageKey) => snackbars.showSuccess(t(messageKey)),
            onError: (messageKey) => snackbars.showError(t(messageKey)),
        });

        const actionPlansTableColumns = useActionPlansTableColumns({
            onDeleteActionPlan: (actionPlan) => deletion.setActionPlanToDelete(actionPlan),
        }) as unknown as IMainTableColumn[];

        const handleAfterInlineSave = async () => {
            if (onActionPlanCreated) {
                await onActionPlanCreated();
            }
            snackbars.showSuccess(t("rca.action_plans.action_plan_details.update_success"));
        };

        const handleInlineEditError = () => {
            snackbars.showError(t("rca.action_plans.action_plan_details.update_failed"));
        };

        return (
            <Card ref={ref} className={`${mainClasses.contentCard} ${classes.card}`}>
                <div className={mainClasses.contentCardHeader}>
                    <div className={mainClasses.contentCardTitle}>
                        {t("rca.rca_library.rca_detail.action_plans.title")}
                    </div>
                    {RCA_PERMISSIONS.action_plans.canCreate && (
                        <div className={mainClasses.contentCardActions}>
                            {!creation.isInlineFormVisible ? (
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS.action_plans.canCreate}
                                    title={t("rca.rca_library.rca_detail.action_plans.add_action_plan")}
                                    renderIcon={() => <AddCircleIcon />}
                                    onClick={() => {
                                        creation.setIsInlineFormVisible(true);
                                    }}
                                />
                            ) : (
                                <MainCardButtonAction
                                    disabled={creation.isCreatingActionPlan}
                                    title={t("rca.rca_library.rca_detail.action_plans.cancel_creation")}
                                    renderIcon={() => <CancelIcon />}
                                    onClick={creation.handleCancelCreation}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className={classes.cardContent}>
                    <div className={classes.inlineFormWrapper}>
                        {RCA_PERMISSIONS.action_plans.canCreate && (
                            <ActionPlanInlineForm
                                isVisible={creation.isInlineFormVisible}
                                isCreating={creation.isCreatingActionPlan}
                                millId={millId}
                                formRef={creation.inlineFormRef}
                                actionPlanNameInputRef={creation.actionPlanNameInputRef}
                                onSubmit={creation.handleCreate}
                            />
                        )}
                    </div>
                    {(!rows || !rows.length) && <NoData />}
                    {!!rows && !!rows.length && (
                        <ActionPlansEditionProvider onAfterSave={handleAfterInlineSave} onError={handleInlineEditError}>
                            <ActionPlansMainTable
                                columns={actionPlansTableColumns}
                                rows={rows}
                                loading={loading}
                                parentType={parentType}
                                parentId={parentId}
                            />
                        </ActionPlansEditionProvider>
                    )}
                </div>
                <Snackbar
                    open={snackbars.snackbarMessage !== ""}
                    onClose={snackbars.closeSuccess}
                    autoHideDuration={6000}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Alert severity="success" sx={{ width: "100%" }}>
                        {snackbars.snackbarMessage}
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={snackbars.snackbarErrorMessage !== ""}
                    onClose={snackbars.closeError}
                    autoHideDuration={6000}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Alert severity="error" sx={{ width: "100%" }}>
                        {snackbars.snackbarErrorMessage}
                    </Alert>
                </Snackbar>
                <RCAConfirmDeleteModal
                    open={!!deletion.actionPlanToDelete}
                    onClose={() => deletion.setActionPlanToDelete(null)}
                    onDeleteItem={deletion.handleDeleteActionPlan}
                    title={t("rca.action_plans.action_plan_delete_modal.title")}
                    warning={t("rca.action_plans.action_plan_delete_modal.warning")}
                    confirm={t("rca.action_plans.action_plan_delete_modal.confirm")}
                />
            </Card>
        );
    },
);

ActionPlansTable.displayName = "ActionPlansTable";

export default ActionPlansTable;
